import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Plus, Pencil, Trash2, LogOut, Package, Layers, CreditCard, X, Gem, Flame, Palette, ShoppingCart, FileText } from "lucide-react";
import { formatEur, t } from "@/i18n/sk";
import { api, getProducts, getExpansions, getCards, getEras, getSettings, updateSettings, getOrders, updateOrderStatus, getAllContent, updateContent, resolveImg } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ImageUploader } from "@/components/admin/ImageUploader";

const THEMES = [
  { id: "rarecandy-light", name: "RareCandy Light", c1: "#7C3AED", c2: "#06B6D4", bg: "#F6F5FB" },
  { id: "violet-cyan", name: "Violet & Cyan", c1: "#6D28D9", c2: "#0891B2", bg: "#F4F6FB" },
  { id: "light-gold", name: "Light & Gold", c1: "#B78628", c2: "#D4A94A", bg: "#FBF8F1" },
  { id: "light-crimson", name: "Light & Crimson", c1: "#DC2626", c2: "#F43F5E", bg: "#FBF5F5" },
];

function AppearanceTab() {
  const { refresh, applyTheme, settings: ctxSettings } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const originalTheme = useRef(ctxSettings?.theme || "rarecandy-light");

  useEffect(() => {
    getSettings().then((d) => { setS(d); originalTheme.current = d.theme; }).catch(() => {});
  }, []);

  if (!s) return <div className="py-16 text-center text-slate-500">Načítavam…</div>;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const previewTheme = (id) => { set("theme", id); applyTheme(id); };

  const save = async () => {
    setSaving(true);
    try {
      await updateSettings(s);
      await refresh();
      originalTheme.current = s.theme;
      toast.success("Vzhľad uložený");
    } catch (e) { toast.error(e.response?.data?.detail || "Chyba pri ukladaní"); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h3 className="mb-1 font-display text-lg font-bold text-white">Farebná téma</h3>
        <p className="mb-4 text-sm text-slate-400">Vyber tému — zmena sa okamžite zobrazí ako náhľad. Ulož ju tlačidlom nižšie.</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {THEMES.map((th) => (
            <button key={th.id} type="button" onClick={() => previewTheme(th.id)} data-testid={`theme-${th.id}`} className={`rounded-2xl border p-4 text-left transition-all ${s.theme === th.id ? "border-violet-glow ring-2 ring-violet-glow/40" : "border-white/10 hover:border-white/25"}`}>
              <div className="mb-3 flex h-16 w-full items-center gap-2 overflow-hidden rounded-lg" style={{ background: th.bg }}>
                <span className="ml-3 h-8 w-8 rounded-full" style={{ background: th.c1 }} />
                <span className="h-8 w-8 rounded-full" style={{ background: th.c2 }} />
              </div>
              <div className="text-sm font-semibold text-white">{th.name}</div>
              {s.theme === th.id && <div className="mt-1 text-xs text-cyan-300">Aktívna</div>}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-8 sm:grid-cols-3">
        <div className="rounded-2xl border border-white/8 bg-vault-card p-5"><ImageUploader value={s.logo_url ? [s.logo_url] : []} onChange={(i) => set("logo_url", i[0] || "")} multiple={false} label="Logo (hlavička)" /></div>
        <div className="rounded-2xl border border-white/8 bg-vault-card p-5"><ImageUploader value={s.hero_image_url ? [s.hero_image_url] : []} onChange={(i) => set("hero_image_url", i[0] || "")} multiple={false} label="Hero obrázok" /></div>
        <div className="rounded-2xl border border-white/8 bg-vault-card p-5"><ImageUploader value={s.hero_bg_url ? [s.hero_bg_url] : []} onChange={(i) => set("hero_bg_url", i[0] || "")} multiple={false} label="Hero pozadie" /></div>
      </div>

      <div>
        <h3 className="mb-1 font-display text-lg font-bold text-white">Doprava</h3>
        <p className="mb-4 text-sm text-slate-400">Nastav cenu dopravy (Packeta) a hranicu pre dopravu zadarmo. Uplatní sa v košíku aj pri platbe.</p>
        <div className="grid max-w-md gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Cena dopravy (€)</label>
            <input type="number" step="0.01" min="0" data-testid="settings-shipping-fee" value={s.shipping_fee_eur ?? 0} onChange={(e) => set("shipping_fee_eur", parseFloat(e.target.value) || 0)} className="w-full rounded-lg border border-white/10 bg-vault-black/60 px-3 py-2.5 text-sm text-white focus:border-cyan-glow/50 focus:outline-none" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Zadarmo nad (€)</label>
            <input type="number" step="0.01" min="0" data-testid="settings-free-threshold" value={s.free_shipping_threshold_eur ?? 0} onChange={(e) => set("free_shipping_threshold_eur", parseFloat(e.target.value) || 0)} className="w-full rounded-lg border border-white/10 bg-vault-black/60 px-3 py-2.5 text-sm text-white focus:border-cyan-glow/50 focus:outline-none" />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} data-testid="settings-save-button" className="btn-glow rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-7 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{saving ? "Ukladám…" : "Uložiť nastavenia"}</button>
        <button onClick={() => { previewTheme(originalTheme.current); getSettings().then(setS); }} className="rounded-full border border-white/10 px-5 py-2.5 text-sm text-slate-200 hover:bg-white/5">Zrušiť náhľad</button>
      </div>
    </div>
  );
}

const TYPES = ["etb", "booster_box", "booster_pack"];
const AVAIL = ["in_stock", "low_stock", "preorder", "sold_out"];
const field = "w-full rounded-lg border border-white/10 bg-vault-black/60 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-cyan-glow/50 focus:outline-none";
const label = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400";

function BrandMark({ sub }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-violet-glow to-cyan-glow"><Gem className="h-5 w-5 text-white" strokeWidth={2.2} /></span>
      <div>
        <div className="font-display text-sm font-bold text-white">RareCandy.sk</div>
        <div className="text-xs text-cyan-300">{sub}</div>
      </div>
    </div>
  );
}

function Login({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/login", { password });
      toast.success("Prihlásenie úspešné");
      onSuccess();
    } catch { toast.error("Nesprávne heslo"); } finally { setLoading(false); }
  };
  return (
    <div className="vault-bg flex min-h-screen items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/10 bg-vault-card p-8">
        <div className="mb-6"><BrandMark sub="Administrácia" /></div>
        <label className={label}>Heslo</label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" placeholder="Zadaj heslo" className={`${field} pl-9`} autoFocus />
        </div>
        <button type="submit" disabled={loading} data-testid="admin-login-button" className="btn-glow mt-5 w-full rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow py-3 text-sm font-semibold text-white disabled:opacity-60">{loading ? "Prihlasujem…" : "Prihlásiť sa"}</button>
        <Link to="/" className="mt-4 block text-center text-xs text-slate-500 hover:text-slate-300">← Späť na obchod</Link>
      </form>
    </div>
  );
}

function ModalShell({ title, onClose, children, onSubmit, saving, width = "max-w-2xl" }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-4">
      <div className="absolute inset-0 bg-vault-black/80 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={onSubmit} className={`relative my-8 w-full ${width} rounded-2xl border border-white/10 bg-vault-midnight p-6`}>
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-white">{title}</h3>
          <button type="button" onClick={onClose} data-testid="modal-close" className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        {children}
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-full border border-white/10 px-5 py-2.5 text-sm text-slate-200 hover:bg-white/5">Zrušiť</button>
          <button type="submit" disabled={saving} data-testid="modal-save-button" className="btn-glow rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{saving ? "Ukladám…" : "Uložiť"}</button>
        </div>
      </form>
    </div>
  );
}

function ProductModal({ product, expansions, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: product?.name || "", expansion_slug: product?.expansion_slug || expansions[0]?.slug || "",
    product_type: product?.product_type || "etb", price_eur: product?.price_eur ?? "",
    stock: product?.stock ?? 0, availability: product?.availability || "in_stock",
    description: product?.description || "", images: product?.images || [],
    placeholder_image: product?.placeholder_image || false, is_new: product?.is_new || false,
    is_featured: product?.is_featured || false, order: product?.order ?? 0,
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.price_eur === "" || Number(form.price_eur) < 0) { toast.error("Vyplň názov a platnú cenu"); return; }
    setSaving(true);
    const payload = { ...form, price_eur: Number(form.price_eur), stock: Number(form.stock), order: Number(form.order) };
    try {
      if (product) { await api.put(`/admin/products/${product.id}`, payload); toast.success("Produkt upravený"); }
      else { await api.post("/admin/products", payload); toast.success("Produkt vytvorený"); }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Chyba pri ukladaní"); } finally { setSaving(false); }
  };
  return (
    <ModalShell title={product ? "Upraviť produkt" : "Nový produkt"} onClose={onClose} onSubmit={submit} saving={saving}>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><label className={label}>Názov</label><input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pf-name" className={field} /></div>
        <div><label className={label}>Séria</label><select value={form.expansion_slug} onChange={(e) => set("expansion_slug", e.target.value)} data-testid="pf-expansion" className={field}>{expansions.map((ex) => <option key={ex.slug} value={ex.slug}>{ex.name}</option>)}</select></div>
        <div><label className={label}>Typ produktu</label><select value={form.product_type} onChange={(e) => set("product_type", e.target.value)} data-testid="pf-type" className={field}>{TYPES.map((ty) => <option key={ty} value={ty}>{t.productTypes[ty]}</option>)}</select></div>
        <div><label className={label}>Cena (EUR)</label><input type="number" step="0.01" min="0" value={form.price_eur} onChange={(e) => set("price_eur", e.target.value)} data-testid="pf-price" className={field} /></div>
        <div><label className={label}>Sklad (ks)</label><input type="number" min="0" value={form.stock} onChange={(e) => set("stock", e.target.value)} data-testid="pf-stock" className={field} /></div>
        <div><label className={label}>Dostupnosť</label><select value={form.availability} onChange={(e) => set("availability", e.target.value)} data-testid="pf-availability" className={field}>{AVAIL.map((a) => <option key={a} value={a}>{t.availability[a]}</option>)}</select></div>
        <div><label className={label}>Poradie</label><input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className={field} /></div>
        <div className="sm:col-span-2"><label className={label}>Popis</label><textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="pf-description" rows={3} className={field} /></div>
        <div className="sm:col-span-2"><ImageUploader value={form.images} onChange={(imgs) => set("images", imgs)} /></div>
        <div className="flex flex-wrap gap-5 sm:col-span-2">
          {[["placeholder_image", "Ilustračné foto"], ["is_new", "Novinka"], ["is_featured", "Odporúčané"]].map(([k, lbl]) => (
            <label key={k} className="flex cursor-pointer items-center gap-2 text-sm text-slate-200"><input type="checkbox" checked={form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`pf-${k}`} className="h-4 w-4 accent-violet-glow" />{lbl}</label>
          ))}
        </div>
      </div>
    </ModalShell>
  );
}

function CardModal({ card, expansions, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: card?.name || "", expansion_slug: card?.expansion_slug || expansions[0]?.slug || "",
    card_number: card?.card_number || "", rarity: card?.rarity || "", language: card?.language || "", condition: card?.condition || "",
    price_eur: card?.price_eur ?? "", stock: card?.stock ?? 0, availability: card?.availability || "in_stock",
    description: card?.description || "", images: card?.images || [], placeholder_image: card?.placeholder_image || false,
    is_new: card?.is_new || false, is_featured: card?.is_featured || false, order: card?.order ?? 0,
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Vyplň názov karty"); return; }
    setSaving(true);
    const payload = { ...form, price_eur: form.price_eur === "" ? null : Number(form.price_eur), stock: Number(form.stock), order: Number(form.order) };
    try {
      if (card) { await api.put(`/admin/cards/${card.id}`, payload); toast.success("Karta upravená"); }
      else { await api.post("/admin/cards", payload); toast.success("Karta vytvorená"); }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Chyba pri ukladaní"); } finally { setSaving(false); }
  };
  return (
    <ModalShell title={card ? "Upraviť kartu" : "Nová karta"} onClose={onClose} onSubmit={submit} saving={saving}>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><label className={label}>Názov karty</label><input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="cf-name" className={field} /></div>
        <div><label className={label}>Séria</label><select value={form.expansion_slug} onChange={(e) => set("expansion_slug", e.target.value)} data-testid="cf-expansion" className={field}>{expansions.map((ex) => <option key={ex.slug} value={ex.slug}>{ex.name}</option>)}</select></div>
        <div><label className={label}>Číslo karty</label><input value={form.card_number} onChange={(e) => set("card_number", e.target.value)} data-testid="cf-number" placeholder="napr. 025/198" className={field} /></div>
        <div><label className={label}>Rarita</label><input value={form.rarity} onChange={(e) => set("rarity", e.target.value)} data-testid="cf-rarity" placeholder="napr. Illustration Rare" className={field} /></div>
        <div><label className={label}>Jazyk</label><input value={form.language} onChange={(e) => set("language", e.target.value)} data-testid="cf-language" placeholder="napr. EN / JP" className={field} /></div>
        <div><label className={label}>Stav</label><input value={form.condition} onChange={(e) => set("condition", e.target.value)} data-testid="cf-condition" placeholder="napr. NM / Mint" className={field} /></div>
        <div><label className={label}>Cena (EUR) — nepovinné</label><input type="number" step="0.01" min="0" value={form.price_eur} onChange={(e) => set("price_eur", e.target.value)} data-testid="cf-price" placeholder="prázdne = na vyžiadanie" className={field} /></div>
        <div><label className={label}>Sklad (ks)</label><input type="number" min="0" value={form.stock} onChange={(e) => set("stock", e.target.value)} className={field} /></div>
        <div><label className={label}>Dostupnosť</label><select value={form.availability} onChange={(e) => set("availability", e.target.value)} className={field}>{AVAIL.map((a) => <option key={a} value={a}>{t.availability[a]}</option>)}</select></div>
        <div className="sm:col-span-2"><label className={label}>Popis</label><textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className={field} /></div>
        <div className="sm:col-span-2"><ImageUploader value={form.images} onChange={(imgs) => set("images", imgs)} /></div>
        <div className="flex flex-wrap gap-5 sm:col-span-2">
          {[["placeholder_image", "Ilustračné foto"], ["is_new", "Novinka"], ["is_featured", "Odporúčané"]].map(([k, lbl]) => (
            <label key={k} className="flex cursor-pointer items-center gap-2 text-sm text-slate-200"><input type="checkbox" checked={form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`cf-${k}`} className="h-4 w-4 accent-violet-glow" />{lbl}</label>
          ))}
        </div>
      </div>
    </ModalShell>
  );
}

function ExpansionModal({ expansion, eras, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: expansion?.name || "", era_slug: expansion?.era_slug || "", series: expansion?.series || "", release_year: expansion?.release_year ?? new Date().getFullYear(),
    release_label: expansion?.release_label || "", release_date: expansion?.release_date || "", badge: expansion?.badge || "", description: expansion?.description || "",
    image_url: expansion?.image_url || "", placeholder_image: expansion?.placeholder_image || false, order: expansion?.order ?? 0,
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Vyplň názov série"); return; }
    setSaving(true);
    const payload = { ...form, release_year: Number(form.release_year), order: Number(form.order) };
    try {
      if (expansion) { await api.put(`/admin/expansions/${expansion.id}`, payload); toast.success("Séria upravená"); }
      else { await api.post("/admin/expansions", payload); toast.success("Séria vytvorená"); }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Chyba pri ukladaní"); } finally { setSaving(false); }
  };
  return (
    <ModalShell title={expansion ? "Upraviť sériu" : "Nová séria"} onClose={onClose} onSubmit={submit} saving={saving} width="max-w-xl">
      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className={label}>Názov</label><input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="ef-name" className={field} /></div>
        <div><label className={label}>Éra</label><select value={form.era_slug} onChange={(e) => set("era_slug", e.target.value)} data-testid="ef-era" className={field}><option value="">— bez éry —</option>{eras.map((er) => <option key={er.slug} value={er.slug}>{er.name}</option>)}</select></div>
        <div><label className={label}>Rok vydania</label><input type="number" value={form.release_year} onChange={(e) => set("release_year", e.target.value)} className={field} /></div>
        <div><label className={label}>Dátum vydania</label><input type="date" value={form.release_date} onChange={(e) => set("release_date", e.target.value)} data-testid="ef-release-date" className={field} /></div>
        <div><label className={label}>Štítok (badge)</label><input value={form.badge} onChange={(e) => set("badge", e.target.value)} data-testid="ef-badge" className={field} placeholder="napr. Novinka / Už čoskoro" /></div>
        <div><label className={label}>Dátum / popisok</label><input value={form.release_label} onChange={(e) => set("release_label", e.target.value)} className={field} placeholder="napr. 26. september 2025" /></div>
        <div className="sm:col-span-2"><label className={label}>Popis</label><textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className={field} /></div>
        <div className="sm:col-span-2"><ImageUploader value={form.image_url ? [form.image_url] : []} onChange={(imgs) => set("image_url", imgs[0] || "")} multiple={false} label="Obrázok série" /></div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-200 sm:col-span-2"><input type="checkbox" checked={form.placeholder_image} onChange={(e) => set("placeholder_image", e.target.checked)} className="h-4 w-4 accent-violet-glow" />Ilustračné foto</label>
      </div>
    </ModalShell>
  );
}

function EraModal({ era, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: era?.name || "", description: era?.description || "", image_url: era?.image_url || "",
    placeholder_image: era?.placeholder_image || false, order: era?.order ?? 0,
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Vyplň názov éry"); return; }
    setSaving(true);
    const payload = { ...form, order: Number(form.order) };
    try {
      if (era) { await api.put(`/admin/eras/${era.id}`, payload); toast.success("Éra upravená"); }
      else { await api.post("/admin/eras", payload); toast.success("Éra vytvorená"); }
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Chyba pri ukladaní"); } finally { setSaving(false); }
  };
  return (
    <ModalShell title={era ? "Upraviť éru" : "Nová éra"} onClose={onClose} onSubmit={submit} saving={saving} width="max-w-xl">
      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className={label}>Názov</label><input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="erf-name" className={field} placeholder="napr. Mega Evolution" /></div>
        <div><label className={label}>Poradie</label><input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className={field} /></div>
        <div className="sm:col-span-2"><label className={label}>Popis</label><textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className={field} /></div>
        <div className="sm:col-span-2"><ImageUploader value={form.image_url ? [form.image_url] : []} onChange={(imgs) => set("image_url", imgs[0] || "")} multiple={false} label="Obrázok éry" /></div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-200 sm:col-span-2"><input type="checkbox" checked={form.placeholder_image} onChange={(e) => set("placeholder_image", e.target.checked)} className="h-4 w-4 accent-violet-glow" />Ilustračné foto</label>
      </div>
    </ModalShell>
  );
}

const FULFILL = {
  received: { label: "Prijatá", cls: "text-cyan-300 border-cyan-glow/30 bg-cyan-glow/10" },
  processing: { label: "Spracúva sa", cls: "text-amber-300 border-amber-400/30 bg-amber-400/10" },
  shipped: { label: "Odoslaná", cls: "text-violet-200 border-violet-glow/30 bg-violet-glow/10" },
  delivered: { label: "Doručená", cls: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" },
  cancelled: { label: "Zrušená", cls: "text-rose-300 border-rose-400/30 bg-rose-400/10" },
};
const PAYMENT = {
  pending: { label: "Čaká na platbu", cls: "text-slate-300" },
  paid: { label: "Zaplatená", cls: "text-emerald-300" },
  failed: { label: "Zlyhala", cls: "text-rose-300" },
  expired: { label: "Expirovala", cls: "text-slate-400" },
};

function ContentTab() {
  const PAGES = [
    { key: "shipping", label: "Doprava a platba" },
    { key: "returns", label: "Vrátenie tovaru" },
    { key: "terms", label: "Obchodné podmienky" },
    { key: "privacy", label: "Ochrana osobných údajov" },
  ];
  const [pages, setPages] = useState(null);
  const [active, setActive] = useState("shipping");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getAllContent().then(setPages).catch(() => setPages([])); }, []);
  useEffect(() => {
    if (pages) { const p = pages.find((x) => x.key === active); setDraft(p ? JSON.parse(JSON.stringify(p)) : null); }
  }, [pages, active]);

  const save = async () => {
    setSaving(true);
    try {
      const { title, intro, notice, sections } = draft;
      const res = await updateContent(active, { title, intro, notice, sections });
      setPages((ps) => ps.map((p) => (p.key === active ? { ...res, key: active } : p)));
      toast.success("Text uložený");
    } catch (e) { toast.error(e.response?.data?.detail || "Chyba pri ukladaní"); }
    setSaving(false);
  };

  const setSec = (i, field, val) => setDraft((d) => { const s = [...d.sections]; s[i] = { ...s[i], [field]: val }; return { ...d, sections: s }; });
  const addSec = () => setDraft((d) => ({ ...d, sections: [...d.sections, { title: "Nová sekcia", body: "" }] }));
  const removeSec = (i) => setDraft((d) => ({ ...d, sections: d.sections.filter((_, j) => j !== i) }));
  const moveSec = (i, dir) => setDraft((d) => {
    const s = [...d.sections]; const j = i + dir;
    if (j < 0 || j >= s.length) return d;
    [s[i], s[j]] = [s[j], s[i]]; return { ...d, sections: s };
  });

  if (pages === null) return <div className="py-16 text-center text-slate-500">Načítavam…</div>;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="font-display text-xl font-bold text-white">Texty / Obsah</h2>
        <p className="mt-1 text-sm text-slate-400">Uprav texty právnych a informačných stránok. Zmeny sa prejavia na webe ihneď po uložení.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {PAGES.map((p) => (
          <button key={p.key} onClick={() => setActive(p.key)} data-testid={`content-tab-${p.key}`}
            className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${active === p.key ? "border-cyan-glow/40 bg-cyan-glow/10 text-cyan-200" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
            {p.label}
          </button>
        ))}
      </div>

      {draft && (
        <div className="flex flex-col gap-5 rounded-2xl border border-white/8 bg-vault-card p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Nadpis stránky</label>
              <input data-testid="content-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                className="w-full rounded-lg border border-white/10 bg-vault-black/60 px-3 py-2.5 text-sm text-white focus:border-cyan-glow/50 focus:outline-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Úvodný text (pod nadpisom)</label>
              <input data-testid="content-intro" value={draft.intro} onChange={(e) => setDraft({ ...draft, intro: e.target.value })}
                className="w-full rounded-lg border border-white/10 bg-vault-black/60 px-3 py-2.5 text-sm text-white focus:border-cyan-glow/50 focus:outline-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Upozornenie (farebný rámček, nepovinné)</label>
              <input data-testid="content-notice" value={draft.notice} onChange={(e) => setDraft({ ...draft, notice: e.target.value })}
                className="w-full rounded-lg border border-white/10 bg-vault-black/60 px-3 py-2.5 text-sm text-white focus:border-cyan-glow/50 focus:outline-none" />
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Sekcie</span>
              <button onClick={addSec} data-testid="content-add-section" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-slate-200 hover:border-cyan-glow/40 hover:text-cyan-300"><Plus className="h-3.5 w-3.5" /> Pridať sekciu</button>
            </div>
            {draft.sections.map((s, i) => (
              <div key={i} className="rounded-xl border border-white/8 bg-vault-black/30 p-4" data-testid={`content-section-${i}`}>
                <div className="mb-2 flex items-center gap-2">
                  <input value={s.title} onChange={(e) => setSec(i, "title", e.target.value)} placeholder="Nadpis sekcie"
                    className="flex-1 rounded-lg border border-white/10 bg-vault-black/60 px-3 py-2 text-sm font-semibold text-white focus:border-cyan-glow/50 focus:outline-none" data-testid={`content-section-title-${i}`} />
                  <button onClick={() => moveSec(i, -1)} className="rounded-md border border-white/10 px-2 py-1.5 text-xs text-slate-400 hover:text-white" title="Posunúť vyššie">↑</button>
                  <button onClick={() => moveSec(i, 1)} className="rounded-md border border-white/10 px-2 py-1.5 text-xs text-slate-400 hover:text-white" title="Posunúť nižšie">↓</button>
                  <button onClick={() => removeSec(i)} data-testid={`content-remove-section-${i}`} className="rounded-md border border-rose-400/20 px-2 py-1.5 text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <textarea value={s.body} onChange={(e) => setSec(i, "body", e.target.value)} rows={4} placeholder="Text sekcie (každý odstavec na nový riadok)"
                  className="w-full rounded-lg border border-white/10 bg-vault-black/60 px-3 py-2.5 text-sm leading-relaxed text-slate-200 focus:border-cyan-glow/50 focus:outline-none" data-testid={`content-section-body-${i}`} />
              </div>
            ))}
          </div>

          <div>
            <button onClick={save} disabled={saving} data-testid="content-save-button" className="btn-glow rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-7 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{saving ? "Ukladám…" : "Uložiť text"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => { getOrders().then(setOrders).catch(() => setOrders([])); }, []);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (order, status) => {
    try { const r = await updateOrderStatus(order.id, status); toast.success(r.email_sent ? "Stav aktualizovaný, zákazníkovi odišiel e-mail" : "Stav aktualizovaný"); load(); setDetail((d) => d && d.id === order.id ? { ...d, fulfillment_status: status } : d); }
    catch (e) { toast.error(e.response?.data?.detail || "Chyba"); }
  };

  if (orders === null) return <div className="py-16 text-center text-slate-500">Načítavam…</div>;
  if (orders.length === 0)
    return <div className="rounded-2xl border border-dashed border-white/12 bg-vault-card/50 py-20 text-center text-slate-400" data-testid="orders-empty">Zatiaľ žiadne objednávky.</div>;

  return (
    <div>
      <div className="overflow-hidden rounded-2xl border border-white/8">
        <table className="w-full text-left text-sm">
          <thead className="bg-vault-card text-xs uppercase tracking-wider text-slate-400"><tr>
            <th className="px-4 py-3">Objednávka</th><th className="hidden px-4 py-3 md:table-cell">Zákazník</th>
            <th className="hidden px-4 py-3 sm:table-cell">Platba</th><th className="px-4 py-3">Stav</th>
            <th className="px-4 py-3">Suma</th><th className="px-4 py-3 text-right">Akcie</th>
          </tr></thead>
          <tbody className="divide-y divide-white/8">
            {orders.map((o) => (
              <tr key={o.id} data-testid={`admin-order-row-${o.order_number}`} className="hover:bg-white/[0.02]">
                <td className="px-4 py-3"><div className="font-mono font-semibold text-white">{o.order_number}</div><div className="text-xs text-slate-400">{new Date(o.created_at).toLocaleString("sk-SK")}</div></td>
                <td className="hidden px-4 py-3 text-slate-300 md:table-cell">{o.customer?.full_name}<div className="text-xs text-slate-500">{o.customer?.email}</div></td>
                <td className={`hidden px-4 py-3 sm:table-cell ${PAYMENT[o.payment_status]?.cls || "text-slate-300"}`}>{PAYMENT[o.payment_status]?.label || o.payment_status}</td>
                <td className="px-4 py-3"><span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${FULFILL[o.fulfillment_status]?.cls}`}>{FULFILL[o.fulfillment_status]?.label || o.fulfillment_status}</span></td>
                <td className="px-4 py-3 font-mono text-white">{formatEur(o.total_eur)}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => setDetail(o)} data-testid={`admin-order-view-${o.order_number}`} className="rounded-lg border border-white/10 px-3 py-1.5 text-xs text-slate-200 hover:border-cyan-glow/40 hover:text-cyan-300">Detail</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-vault-black/75 p-4 backdrop-blur-sm" onClick={() => setDetail(null)}>
          <div className="my-8 w-full max-w-lg rounded-2xl border border-white/10 bg-vault-midnight p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-order-detail">
            <div className="mb-4 flex items-center justify-between">
              <div><div className="font-display text-lg font-bold text-white">{detail.order_number}</div><div className="text-xs text-slate-400">{new Date(detail.created_at).toLocaleString("sk-SK")}</div></div>
              <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="mb-4 rounded-xl border border-white/8 bg-vault-card p-4 text-sm">
              <div className="font-semibold text-white">{detail.customer?.full_name}</div>
              <div className="text-slate-300">{detail.customer?.email} · {detail.customer?.phone}</div>
              <div className="mt-1 text-slate-300">{detail.customer?.address}, {detail.customer?.zip} {detail.customer?.city}, {detail.customer?.country}</div>
              {detail.customer?.note && <div className="mt-2 text-slate-400">Poznámka: {detail.customer.note}</div>}
            </div>
            <ul className="mb-4 flex flex-col gap-2">
              {detail.items?.map((it) => (
                <li key={it.product_id} className="flex justify-between text-sm text-slate-300"><span>{it.quantity} × {it.name}</span><span className="font-mono">{formatEur(it.line_total_eur)}</span></li>
              ))}
            </ul>
            <div className="mb-4 flex flex-col gap-1 border-t border-white/8 pt-3 text-sm">
              <div className="flex justify-between text-slate-300"><span>Medzisúčet</span><span className="font-mono">{formatEur(detail.subtotal_eur)}</span></div>
              <div className="flex justify-between text-slate-300"><span>Doprava</span><span className="font-mono">{detail.shipping_eur === 0 ? "Zadarmo" : formatEur(detail.shipping_eur)}</span></div>
              <div className="flex justify-between font-bold text-white"><span>Spolu</span><span className="font-mono">{formatEur(detail.total_eur)}</span></div>
              <div className="mt-1 text-xs text-slate-400">Platba: <span className={PAYMENT[detail.payment_status]?.cls}>{PAYMENT[detail.payment_status]?.label}</span></div>
            </div>
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Stav vybavenia</div>
              <div className="flex flex-wrap gap-2">
                {Object.keys(FULFILL).map((k) => (
                  <button key={k} onClick={() => changeStatus(detail, k)} data-testid={`order-status-${k}`} className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${detail.fulfillment_status === k ? FULFILL[k].cls : "border-white/10 text-slate-300 hover:bg-white/5"}`}>{FULFILL[k].label}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [cards, setCards] = useState([]);
  const [expansions, setExpansions] = useState([]);
  const [eras, setEras] = useState([]);
  const [productModal, setProductModal] = useState(null);
  const [cardModal, setCardModal] = useState(null);
  const [expansionModal, setExpansionModal] = useState(null);
  const [eraModal, setEraModal] = useState(null);

  const load = useCallback(() => {
    getProducts().then(setProducts).catch(() => {});
    getCards().then(setCards).catch(() => {});
    getExpansions().then(setExpansions).catch(() => {});
    getEras().then(setEras).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const logout = async () => { await api.post("/admin/logout").catch(() => {}); onLogout(); };
  const del = async (kind, item, name) => {
    if (!window.confirm(`Odstrániť „${name}"?`)) return;
    try { await api.delete(`/admin/${kind}/${item.id}`); toast.success("Odstránené"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Chyba"); }
  };

  const tabBtn = (id, Icon, text) => (
    <button onClick={() => setTab(id)} data-testid={`tab-${id}`} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === id ? "bg-violet-glow/20 text-white" : "text-slate-300 hover:bg-white/5"}`}><Icon className="h-4 w-4" /> {text}</button>
  );

  return (
    <div className="vault-bg min-h-screen">
      <header className="border-b border-white/8 glass">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <BrandMark sub="Administrácia katalógu" />
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-slate-300 hover:text-white">Obchod</Link>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-sm text-slate-200 hover:border-rose-400/40 hover:text-rose-300"><LogOut className="h-4 w-4" /> Odhlásiť</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap gap-2">
          {tabBtn("products", Package, `Produkty (${products.length})`)}
          {tabBtn("orders", ShoppingCart, "Objednávky")}
          {tabBtn("cards", CreditCard, `Karty (${cards.length})`)}
          {tabBtn("expansions", Layers, `Série (${expansions.length})`)}
          {tabBtn("eras", Flame, `Éry (${eras.length})`)}
          {tabBtn("texty", FileText, "Texty / Obsah")}
          {tabBtn("vzhlad", Palette, "Vzhľad webu")}
        </div>

        {tab === "orders" && <OrdersTab />}
        {tab === "texty" && <ContentTab />}

        {tab === "products" && (
          <div>
            <div className="mb-4 flex justify-end"><button onClick={() => setProductModal({})} data-testid="admin-product-add-button" className="btn-glow inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-5 py-2.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Pridať produkt</button></div>
            <div className="overflow-hidden rounded-2xl border border-white/8">
              <table className="w-full text-left text-sm">
                <thead className="bg-vault-card text-xs uppercase tracking-wider text-slate-400"><tr><th className="px-4 py-3">Produkt</th><th className="hidden px-4 py-3 md:table-cell">Séria</th><th className="hidden px-4 py-3 sm:table-cell">Typ</th><th className="px-4 py-3">Cena</th><th className="px-4 py-3 text-right">Akcie</th></tr></thead>
                <tbody className="divide-y divide-white/8">
                  {products.map((p) => (
                    <tr key={p.id} data-testid={`admin-product-row-${p.slug}`} className="hover:bg-white/[0.02]">
                      <td className="px-4 py-3"><div className="flex items-center gap-3"><div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/8 bg-vault-black/60 p-1">{p.images?.[0] && <img src={resolveImg(p.images[0])} alt="" className="h-full w-full object-contain" />}</div><span className="font-medium text-white">{p.name}</span></div></td>
                      <td className="hidden px-4 py-3 text-slate-300 md:table-cell">{p.expansion_name}</td>
                      <td className="hidden px-4 py-3 text-slate-300 sm:table-cell">{t.productTypes[p.product_type]}</td>
                      <td className="px-4 py-3 font-mono text-white">{formatEur(p.price_eur)}</td>
                      <td className="px-4 py-3"><div className="flex justify-end gap-2"><button onClick={() => setProductModal({ product: p })} data-testid={`admin-edit-${p.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-slate-300 hover:border-cyan-glow/40 hover:text-cyan-300"><Pencil className="h-4 w-4" /></button><button onClick={() => del("products", p, p.name)} data-testid={`admin-delete-${p.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-slate-300 hover:border-rose-400/40 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "cards" && (
          <div>
            <div className="mb-4 flex justify-end"><button onClick={() => setCardModal({})} data-testid="admin-card-add-button" className="btn-glow inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-5 py-2.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Pridať kartu</button></div>
            {cards.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/12 bg-vault-card/50 py-20 text-center text-slate-400">Zatiaľ žiadne jednotlivé karty. Pridajte prvú kartu.</div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-white/8">
                <table className="w-full text-left text-sm">
                  <thead className="bg-vault-card text-xs uppercase tracking-wider text-slate-400"><tr><th className="px-4 py-3">Karta</th><th className="hidden px-4 py-3 md:table-cell">Séria</th><th className="hidden px-4 py-3 sm:table-cell">Rarita</th><th className="px-4 py-3">Cena</th><th className="px-4 py-3 text-right">Akcie</th></tr></thead>
                  <tbody className="divide-y divide-white/8">
                    {cards.map((c) => (
                      <tr key={c.id} data-testid={`admin-card-row-${c.slug}`} className="hover:bg-white/[0.02]">
                        <td className="px-4 py-3"><div className="flex items-center gap-3"><div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/8 bg-vault-black/60 p-1">{c.images?.[0] && <img src={resolveImg(c.images[0])} alt="" className="h-full w-full object-contain" />}</div><span className="font-medium text-white">{c.name}</span></div></td>
                        <td className="hidden px-4 py-3 text-slate-300 md:table-cell">{c.expansion_name}</td>
                        <td className="hidden px-4 py-3 text-slate-300 sm:table-cell">{c.rarity || "—"}</td>
                        <td className="px-4 py-3 font-mono text-white">{c.price_eur != null ? formatEur(c.price_eur) : "—"}</td>
                        <td className="px-4 py-3"><div className="flex justify-end gap-2"><button onClick={() => setCardModal({ card: c })} data-testid={`admin-card-edit-${c.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-slate-300 hover:border-cyan-glow/40 hover:text-cyan-300"><Pencil className="h-4 w-4" /></button><button onClick={() => del("cards", c, c.name)} data-testid={`admin-card-delete-${c.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-slate-300 hover:border-rose-400/40 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button></div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "expansions" && (
          <div>
            <div className="mb-4 flex justify-end"><button onClick={() => setExpansionModal({})} data-testid="admin-expansion-add-button" className="btn-glow inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-5 py-2.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Pridať sériu</button></div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {expansions.map((ex) => (
                <div key={ex.id} data-testid={`admin-expansion-card-${ex.slug}`} className="rounded-2xl border border-white/8 bg-vault-card p-5">
                  <div className="overline mb-1">{ex.era_name ? `${ex.era_name} · ` : ""}{ex.release_label || ex.release_year}</div>
                  <h3 className="font-display text-lg font-bold text-white">{ex.name}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-slate-400">{ex.description}</p>
                  <div className="mt-4 flex gap-2"><button onClick={() => setExpansionModal({ expansion: ex })} data-testid={`admin-edit-expansion-${ex.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-slate-300 hover:border-cyan-glow/40 hover:text-cyan-300"><Pencil className="h-4 w-4" /></button><button onClick={() => del("expansions", ex, ex.name)} data-testid={`admin-delete-expansion-${ex.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-slate-300 hover:border-rose-400/40 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button></div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "eras" && (
          <div>
            <div className="mb-4 flex justify-end"><button onClick={() => setEraModal({})} data-testid="admin-era-add-button" className="btn-glow inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow px-5 py-2.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Pridať éru</button></div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {eras.map((er) => {
                const count = expansions.filter((e) => e.era_slug === er.slug).length;
                return (
                  <div key={er.id} data-testid={`admin-era-card-${er.slug}`} className="rounded-2xl border border-white/8 bg-vault-card p-5">
                    <div className="overline mb-1">Éra · {count} sérií</div>
                    <h3 className="font-display text-lg font-bold text-white">{er.name}</h3>
                    <p className="mt-2 line-clamp-2 text-sm text-slate-400">{er.description}</p>
                    <div className="mt-4 flex gap-2">
                      <button onClick={() => setEraModal({ era: er })} data-testid={`admin-edit-era-${er.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-slate-300 hover:border-cyan-glow/40 hover:text-cyan-300"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => del("eras", er, er.name)} data-testid={`admin-delete-era-${er.slug}`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-slate-300 hover:border-rose-400/40 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {tab === "vzhlad" && <AppearanceTab />}
      </div>

      {productModal && <ProductModal product={productModal.product} expansions={expansions} onClose={() => setProductModal(null)} onSaved={load} />}
      {cardModal && <CardModal card={cardModal.card} expansions={expansions} onClose={() => setCardModal(null)} onSaved={load} />}
      {expansionModal && <ExpansionModal expansion={expansionModal.expansion} eras={eras} onClose={() => setExpansionModal(null)} onSaved={load} />}
      {eraModal && <EraModal era={eraModal.era} onClose={() => setEraModal(null)} onSaved={load} />}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  useEffect(() => { api.get("/admin/me").then((r) => setAuthed(r.data.authenticated)).catch(() => setAuthed(false)); }, []);
  if (authed === null) return <div className="vault-bg min-h-screen" />;
  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => setAuthed(false)} />;
}
